import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import palette from 'src/designSystem/variables/palette'
import {
  DocumentPrivacy,
  FeatureType,
} from '@trustero/trustero-api-web/lib/trust/trust_pb'
import { formatUTCDateAllowUndefined } from 'src/Utils/formatDate'
import { GridColumnSortVisitorAnalytics } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import {
  AnalyticsDashboardName,
  AnalyticsDashboardParam,
  AnalyticsDashboardRequestName,
  AnalyticsGridConfig,
  VisitorAnalyticsDataPoint,
} from './VisitorAnalytics.types'

export const ANALYTICS_GRID_PAGE_SIZE = 10

export const ANALYTICS_COLOR_CONFIG: Record<string, string> = {
  '1': palette.neutral[400],
}

export const FEATURE_TYPE_LABELS: Record<FeatureType, string> = {
  [FeatureType.FEATURE_TYPE_DOCUMENTS]: 'Documents',
  [FeatureType.FEATURE_TYPE_TI]: 'TI',
  [FeatureType.FEATURE_TYPE_QUESTIONNAIRE]: 'Questionnaire',
}

export const PRIVACY_LEVEL_LABELS: Record<DocumentPrivacy, string> = {
  [DocumentPrivacy.PUBLIC]: 'Public',
  [DocumentPrivacy.SEMI_PRIVATE]: 'Confidential',
  [DocumentPrivacy.PRIVATE]: 'Restricted',
}

export const ANALYTICS_DASHBOARD_TO_NAME: Record<
  AnalyticsDashboardParam,
  AnalyticsDashboardName
> = {
  [AnalyticsDashboardParam.ACCESS_REQUESTS]:
    AnalyticsDashboardName.ACCESS_REQUESTS,
  [AnalyticsDashboardParam.GRANTED_REQUESTS]:
    AnalyticsDashboardName.GRANTED_REQUESTS,
  [AnalyticsDashboardParam.DOCUMENT_DOWNLOADS]:
    AnalyticsDashboardName.DOCUMENT_DOWNLOADS,
  [AnalyticsDashboardParam.FAQ_CLICKS]: AnalyticsDashboardName.FAQ_CLICKS,
  [AnalyticsDashboardParam.SEARCHES]: AnalyticsDashboardName.SEARCHES,
  [AnalyticsDashboardParam.TI_CHATS]: AnalyticsDashboardName.TI_CHATS,
  [AnalyticsDashboardParam.TI_QUESTIONS]: AnalyticsDashboardName.TI_QUESTIONS,
  [AnalyticsDashboardParam.UPDATE_NOTIFICATION_SUBSCRIPTIONS]:
    AnalyticsDashboardName.UPDATE_NOTIFICATION_SUBSCRIPTIONS,
  [AnalyticsDashboardParam.UPDATE_NOTIFICATION_UNSUBSCRIPTIONS]:
    AnalyticsDashboardName.UPDATE_NOTIFICATION_UNSUBSCRIPTIONS,
}

export const ANALYTICS_DASHBOARD_TO_REQUEST_NAME: Record<
  AnalyticsDashboardParam,
  AnalyticsDashboardRequestName
> = {
  [AnalyticsDashboardParam.ACCESS_REQUESTS]:
    AnalyticsDashboardRequestName.TRUST_PORTAL_ACCESS_REQUESTS,
  [AnalyticsDashboardParam.GRANTED_REQUESTS]:
    AnalyticsDashboardRequestName.TRUST_PORTAL_GRANTED_REQUESTS,
  [AnalyticsDashboardParam.DOCUMENT_DOWNLOADS]:
    AnalyticsDashboardRequestName.TRUST_PORTAL_DOCUMENT_DOWNLOADS,
  [AnalyticsDashboardParam.FAQ_CLICKS]:
    AnalyticsDashboardRequestName.TRUST_PORTAL_FAQ_CLICKS,
  [AnalyticsDashboardParam.SEARCHES]:
    AnalyticsDashboardRequestName.TRUST_PORTAL_SEARCHES,
  [AnalyticsDashboardParam.TI_CHATS]:
    AnalyticsDashboardRequestName.TRUST_PORTAL_TI_CHATS,
  [AnalyticsDashboardParam.TI_QUESTIONS]:
    AnalyticsDashboardRequestName.TRUST_PORTAL_TI_QUESTIONS,
  [AnalyticsDashboardParam.UPDATE_NOTIFICATION_SUBSCRIPTIONS]:
    AnalyticsDashboardRequestName.TRUST_PORTAL_UPDATE_SUBSCRIPTIONS,
  [AnalyticsDashboardParam.UPDATE_NOTIFICATION_UNSUBSCRIPTIONS]:
    AnalyticsDashboardRequestName.TRUST_PORTAL_UPDATE_UNSUBSCRIPTIONS,
}

export const ANALYTICS_DASHBOARD_TO_INDEX_PAGE_WITH_FILTERS: Record<
  AnalyticsDashboardParam,
  INDEX_PAGE_WITH_FILTERS
> = {
  [AnalyticsDashboardParam.ACCESS_REQUESTS]:
    INDEX_PAGE_WITH_FILTERS.TP_ACCESS_REQUESTS_DASHBOARD,
  [AnalyticsDashboardParam.GRANTED_REQUESTS]:
    INDEX_PAGE_WITH_FILTERS.TP_GRANTED_REQUESTS_DASHBOARD,
  [AnalyticsDashboardParam.DOCUMENT_DOWNLOADS]:
    INDEX_PAGE_WITH_FILTERS.TP_DOCUMENT_DOWNLOADS_DASHBOARD,
  [AnalyticsDashboardParam.FAQ_CLICKS]:
    INDEX_PAGE_WITH_FILTERS.TP_FAQ_CLICKS_DASHBOARD,
  [AnalyticsDashboardParam.SEARCHES]:
    INDEX_PAGE_WITH_FILTERS.TP_SEARCHES_DASHBOARD,
  [AnalyticsDashboardParam.TI_CHATS]:
    INDEX_PAGE_WITH_FILTERS.TP_TI_CHATS_DASHBOARD,
  [AnalyticsDashboardParam.TI_QUESTIONS]:
    INDEX_PAGE_WITH_FILTERS.TP_TI_QUESTIONS_DASHBOARD,
  [AnalyticsDashboardParam.UPDATE_NOTIFICATION_SUBSCRIPTIONS]:
    INDEX_PAGE_WITH_FILTERS.TP_UPDATE_NOTIFICATION_SUBSCRIPTIONS_DASHBOARD,
  [AnalyticsDashboardParam.UPDATE_NOTIFICATION_UNSUBSCRIPTIONS]:
    INDEX_PAGE_WITH_FILTERS.TP_UPDATE_NOTIFICATION_UNSUBSCRIPTIONS_DASHBOARD,
}

export const ANALYTICS_DASHBOARD_TO_GRID_CONFIG: Record<
  AnalyticsDashboardParam,
  AnalyticsGridConfig
> = {
  [AnalyticsDashboardParam.ACCESS_REQUESTS]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    headers: [
      {
        name: 'Name',
        value: GridColumnSortVisitorAnalytics.NAME,
      },
      {
        name: 'Email',
        value: GridColumnSortVisitorAnalytics.EMAIL,
      },
      {
        name: 'Company',
        value: GridColumnSortVisitorAnalytics.COMPANY,
      },
      {
        name: 'Requested',
        value: GridColumnSortVisitorAnalytics.FEATURES,
      },
      {
        name: 'Date',
        value: GridColumnSortVisitorAnalytics.DATE,
      },
    ],
    dataFormatters: {
      Name: (item: VisitorAnalyticsDataPoint) => item.name || 'Anonymous',
      Email: (item: VisitorAnalyticsDataPoint) => item.email || 'Unknown',
      Company: (item: VisitorAnalyticsDataPoint) => item.org_name || 'Unknown',
      Requested: (item: VisitorAnalyticsDataPoint) =>
        item.features
          .map((feature: FeatureType) => FEATURE_TYPE_LABELS[feature])
          .join(', ') || 'Unknown',
      Date: (item: VisitorAnalyticsDataPoint) =>
        formatUTCDateAllowUndefined(
          new Date(item.timestamp),
          DATE_FORMATS.SHORT,
        ) || 'Unknown',
    },
  },
  [AnalyticsDashboardParam.GRANTED_REQUESTS]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    headers: [
      {
        name: 'Name',
        value: GridColumnSortVisitorAnalytics.NAME,
      },
      {
        name: 'Email',
        value: GridColumnSortVisitorAnalytics.EMAIL,
      },
      {
        name: 'Company',
        value: GridColumnSortVisitorAnalytics.COMPANY,
      },
      {
        name: 'Granted requests',
        value: GridColumnSortVisitorAnalytics.FEATURES,
      },
      {
        name: 'Date',
        value: GridColumnSortVisitorAnalytics.DATE,
      },
    ],
    dataFormatters: {
      Name: (item: VisitorAnalyticsDataPoint) => item.name || 'Anonymous',
      Email: (item: VisitorAnalyticsDataPoint) => item.email || 'Unknown',
      Company: (item: VisitorAnalyticsDataPoint) => item.org_name || 'Unknown',
      'Granted requests': (item: VisitorAnalyticsDataPoint) =>
        item.features
          .map((feature: FeatureType) => FEATURE_TYPE_LABELS[feature])
          .join(', ') || 'Unknown',
      Date: (item: VisitorAnalyticsDataPoint) =>
        formatUTCDateAllowUndefined(
          new Date(item.timestamp),
          DATE_FORMATS.SHORT,
        ) || 'Unknown',
    },
  },
  [AnalyticsDashboardParam.DOCUMENT_DOWNLOADS]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
    headers: [
      {
        name: 'Name',
        value: GridColumnSortVisitorAnalytics.NAME,
      },
      {
        name: 'Email',
        value: GridColumnSortVisitorAnalytics.EMAIL,
      },
      {
        name: 'Company',
        value: GridColumnSortVisitorAnalytics.COMPANY,
      },
      {
        name: 'Document name',
        value: GridColumnSortVisitorAnalytics.DOCUMENT_NAME,
      },
      {
        name: 'Access level',
        value: GridColumnSortVisitorAnalytics.ACCESS_LEVEL,
      },
      {
        name: 'Date',
        value: GridColumnSortVisitorAnalytics.DATE,
      },
    ],
    dataFormatters: {
      Name: (item: VisitorAnalyticsDataPoint) => item.name || 'Anonymous',
      Email: (item: VisitorAnalyticsDataPoint) => item.email || 'Unknown',
      Company: (item: VisitorAnalyticsDataPoint) => item.org_name || 'Unknown',
      'Document name': (item: VisitorAnalyticsDataPoint) =>
        item.doc_name || 'Unknown',
      'Access level': (item: VisitorAnalyticsDataPoint) =>
        PRIVACY_LEVEL_LABELS[Number(item.privacy_level) as DocumentPrivacy] ||
        'Unknown',
      Date: (item: VisitorAnalyticsDataPoint) =>
        formatUTCDateAllowUndefined(
          new Date(item.timestamp),
          DATE_FORMATS.SHORT,
        ) || 'Unknown',
    },
  },
  [AnalyticsDashboardParam.FAQ_CLICKS]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    headers: [
      {
        name: 'Name',
        value: GridColumnSortVisitorAnalytics.NAME,
      },
      {
        name: 'Email',
        value: GridColumnSortVisitorAnalytics.EMAIL,
      },
      {
        name: 'Company',
        value: GridColumnSortVisitorAnalytics.COMPANY,
      },
      {
        name: 'FAQ title',
        value: GridColumnSortVisitorAnalytics.FAQ_TITLE,
      },
      {
        name: 'Date',
        value: GridColumnSortVisitorAnalytics.DATE,
      },
    ],
    dataFormatters: {
      Name: (item: VisitorAnalyticsDataPoint) => item.name || 'Anonymous',
      Email: (item: VisitorAnalyticsDataPoint) => item.email || 'Unknown',
      Company: (item: VisitorAnalyticsDataPoint) => item.org_name || 'Unknown',
      'FAQ title': (item: VisitorAnalyticsDataPoint) =>
        item.faq_title || 'Unknown',
      Date: (item: VisitorAnalyticsDataPoint) =>
        formatUTCDateAllowUndefined(
          new Date(item.timestamp),
          DATE_FORMATS.SHORT,
        ) || 'Unknown',
    },
  },
  [AnalyticsDashboardParam.SEARCHES]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    headers: [
      {
        name: 'Name',
        value: GridColumnSortVisitorAnalytics.NAME,
      },
      {
        name: 'Email',
        value: GridColumnSortVisitorAnalytics.EMAIL,
      },
      {
        name: 'Company',
        value: GridColumnSortVisitorAnalytics.COMPANY,
      },
      {
        name: 'Search query',
        value: GridColumnSortVisitorAnalytics.SEARCH_QUERY,
      },
      {
        name: 'Date',
        value: GridColumnSortVisitorAnalytics.DATE,
      },
    ],
    dataFormatters: {
      Name: (item: VisitorAnalyticsDataPoint) => item.name || 'Anonymous',
      Email: (item: VisitorAnalyticsDataPoint) => item.email || 'Unknown',
      Company: (item: VisitorAnalyticsDataPoint) => item.org_name || 'Unknown',
      'Search query': (item: VisitorAnalyticsDataPoint) =>
        item.search_query || 'Unknown',
      Date: (item: VisitorAnalyticsDataPoint) =>
        formatUTCDateAllowUndefined(
          new Date(item.timestamp),
          DATE_FORMATS.SHORT,
        ) || 'Unknown',
    },
  },
  [AnalyticsDashboardParam.TI_CHATS]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    headers: [
      {
        name: 'Name',
        value: GridColumnSortVisitorAnalytics.NAME,
      },
      {
        name: 'Email',
        value: GridColumnSortVisitorAnalytics.EMAIL,
      },
      {
        name: 'Company',
        value: GridColumnSortVisitorAnalytics.COMPANY,
      },
      {
        name: 'Date',
        value: GridColumnSortVisitorAnalytics.DATE,
      },
    ],
    dataFormatters: {
      Name: (item: VisitorAnalyticsDataPoint) => item.name || 'Anonymous',
      Email: (item: VisitorAnalyticsDataPoint) => item.email || 'Unknown',
      Company: (item: VisitorAnalyticsDataPoint) => item.org_name || 'Unknown',
      Date: (item: VisitorAnalyticsDataPoint) =>
        formatUTCDateAllowUndefined(
          new Date(item.timestamp),
          DATE_FORMATS.SHORT,
        ) || 'Unknown',
    },
  },
  [AnalyticsDashboardParam.TI_QUESTIONS]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    headers: [
      {
        name: 'Name',
        value: GridColumnSortVisitorAnalytics.NAME,
      },
      {
        name: 'Email',
        value: GridColumnSortVisitorAnalytics.EMAIL,
      },
      {
        name: 'Company',
        value: GridColumnSortVisitorAnalytics.COMPANY,
      },
      {
        name: 'Question',
        value: GridColumnSortVisitorAnalytics.QUESTION,
      },
      {
        name: 'Date',
        value: GridColumnSortVisitorAnalytics.DATE,
      },
    ],
    dataFormatters: {
      Name: (item: VisitorAnalyticsDataPoint) => item.name || 'Anonymous',
      Email: (item: VisitorAnalyticsDataPoint) => item.email || 'Unknown',
      Company: (item: VisitorAnalyticsDataPoint) => item.org_name || 'Unknown',
      Question: (item: VisitorAnalyticsDataPoint) => item.question || 'Unknown',
      Date: (item: VisitorAnalyticsDataPoint) =>
        formatUTCDateAllowUndefined(
          new Date(item.timestamp),
          DATE_FORMATS.SHORT,
        ) || 'Unknown',
    },
  },
  [AnalyticsDashboardParam.UPDATE_NOTIFICATION_SUBSCRIPTIONS]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    headers: [
      {
        name: 'Name',
        value: GridColumnSortVisitorAnalytics.NAME,
      },
      {
        name: 'Email',
        value: GridColumnSortVisitorAnalytics.EMAIL,
      },
      {
        name: 'Company',
        value: GridColumnSortVisitorAnalytics.COMPANY,
      },
      {
        name: 'Category',
        value: GridColumnSortVisitorAnalytics.CATEGORY,
      },
      {
        name: 'Date',
        value: GridColumnSortVisitorAnalytics.DATE,
      },
    ],
    dataFormatters: {
      Name: (item: VisitorAnalyticsDataPoint) => item.name || 'Anonymous',
      Email: (item: VisitorAnalyticsDataPoint) => item.email || 'Unknown',
      Company: (item: VisitorAnalyticsDataPoint) => item.org_name || 'Unknown',
      Category: (item: VisitorAnalyticsDataPoint) => item.category || 'Unknown',
      Date: (item: VisitorAnalyticsDataPoint) =>
        formatUTCDateAllowUndefined(
          new Date(item.timestamp),
          DATE_FORMATS.SHORT,
        ) || 'Unknown',
    },
  },
  [AnalyticsDashboardParam.UPDATE_NOTIFICATION_UNSUBSCRIPTIONS]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    headers: [
      {
        name: 'Name',
        value: GridColumnSortVisitorAnalytics.NAME,
      },
      {
        name: 'Email',
        value: GridColumnSortVisitorAnalytics.EMAIL,
      },
      {
        name: 'Company',
        value: GridColumnSortVisitorAnalytics.COMPANY,
      },
      {
        name: 'Category',
        value: GridColumnSortVisitorAnalytics.CATEGORY,
      },
      {
        name: 'Date',
        value: GridColumnSortVisitorAnalytics.DATE,
      },
    ],
    dataFormatters: {
      Name: (item: VisitorAnalyticsDataPoint) => item.name || 'Anonymous',
      Email: (item: VisitorAnalyticsDataPoint) => item.email || 'Unknown',
      Company: (item: VisitorAnalyticsDataPoint) => item.org_name || 'Unknown',
      Category: (item: VisitorAnalyticsDataPoint) => item.category || 'Unknown',
      Date: (item: VisitorAnalyticsDataPoint) =>
        formatUTCDateAllowUndefined(
          new Date(item.timestamp),
          DATE_FORMATS.SHORT,
        ) || 'Unknown',
    },
  },
}
